import type { LayerSpecification } from 'maplibre-gl'

const TRACK_LINE_ARROW_SPACING: [
  'interpolate',
  ['linear'],
  ['zoom'],
  8,
  number,
  12,
  number,
  16,
  number,
  20,
  number,
] = ['interpolate', ['linear'], ['zoom'], 8, 150, 12, 110, 16, 82, 20, 70]

const TRACK_LINE_ARROW_SIZE: [
  'interpolate',
  ['linear'],
  ['zoom'],
  8,
  number,
  12,
  number,
  16,
  number,
  20,
  number,
] = ['interpolate', ['linear'], ['zoom'], 8, 0.82, 12, 0.9, 16, 1, 20, 1.08]

export const TRACK_LINE_STYLE = {
  outlineWidth: 8,
  outlineOpacity: 0.8,
  lineWidth: 4,
  touchTargetWidth: 20,
  arrowOpacity: 0.85,
  arrowSpacing: TRACK_LINE_ARROW_SPACING,
  arrowSize: TRACK_LINE_ARROW_SIZE,
}

export const TRACK_SURFACE_DASH_STYLE = {
  lineColor: '#ffffff',
  lineWidth: 2.35,
  lineOpacity: 0.92,
  lineDasharray: [0.8, 1.75],
}

export const TRACK_LINE_LAYOUT = {
  'line-join': 'round',
  'line-cap': 'round',
} as const

export type TrackLineColor = string | ['get', string]

export type TrackLineLayerIds = {
  outlineId: string
  lineId: string
  touchTargetId: string
  arrowId: string
}

type TrackLineVisibility = 'visible' | 'none'

type BuildTrackLineLayersOptions = {
  sourceId: string
  ids: TrackLineLayerIds
  lineColor: TrackLineColor
  visibility?: TrackLineVisibility
  includeDirectionArrows?: boolean
}

type BuildTrackDirectionArrowLayerOptions = {
  id: string
  sourceId: string
  visibility?: TrackLineVisibility
  opacity?: number
}

type BuildTrackSurfaceDashLayerOptions = {
  id: string
  sourceId: string
  visibility?: TrackLineVisibility
}

const withVisibility = (visibility?: TrackLineVisibility) =>
  visibility ? { ...TRACK_LINE_LAYOUT, visibility } : { ...TRACK_LINE_LAYOUT }

export const buildTrackLineLayerIds = (prefix: string, baseId: string): TrackLineLayerIds => ({
  outlineId: `${prefix}-outline-${baseId}`,
  lineId: `${prefix}-line-${baseId}`,
  touchTargetId: `${prefix}-touch-${baseId}`,
  arrowId: `${prefix}-arrows-${baseId}`,
})

export const buildTrackDirectionArrowLayer = ({
  id,
  sourceId,
  visibility,
  opacity = TRACK_LINE_STYLE.arrowOpacity,
}: BuildTrackDirectionArrowLayerOptions): LayerSpecification => ({
  id,
  type: 'symbol',
  source: sourceId,
  layout: {
    visibility: visibility ?? 'visible',
    'symbol-placement': 'line',
    'icon-image': 'direction-arrow',
    'icon-rotation-alignment': 'map',
    'icon-keep-upright': false,
    'symbol-spacing': TRACK_LINE_STYLE.arrowSpacing,
    'icon-size': TRACK_LINE_STYLE.arrowSize,
  },
  paint: {
    'icon-opacity': opacity,
  },
})

export const buildTrackSurfaceDashLayer = ({
  id,
  sourceId,
  visibility,
}: BuildTrackSurfaceDashLayerOptions): LayerSpecification => ({
  id,
  type: 'line',
  source: sourceId,
  filter: ['==', ['get', 'surfacePavedStatus'], 'unpaved'],
  layout: {
    'line-join': 'round',
    'line-cap': 'butt',
    visibility: visibility ?? 'visible',
  },
  paint: {
    'line-color': TRACK_SURFACE_DASH_STYLE.lineColor,
    'line-width': TRACK_SURFACE_DASH_STYLE.lineWidth,
    'line-opacity': TRACK_SURFACE_DASH_STYLE.lineOpacity,
    'line-dasharray': TRACK_SURFACE_DASH_STYLE.lineDasharray,
  },
})

export const buildTrackLineLayers = ({
  sourceId,
  ids,
  lineColor,
  visibility,
  includeDirectionArrows = true,
}: BuildTrackLineLayersOptions) => {
  const orderedLayers: LayerSpecification[] = [
    {
      id: ids.touchTargetId,
      type: 'line',
      source: sourceId,
      layout: withVisibility(visibility),
      paint: {
        'line-color': 'transparent',
        'line-width': TRACK_LINE_STYLE.touchTargetWidth,
        'line-opacity': 0,
      },
    },
    {
      id: ids.outlineId,
      type: 'line',
      source: sourceId,
      layout: withVisibility(visibility),
      paint: {
        'line-color': '#ffffff',
        'line-width': TRACK_LINE_STYLE.outlineWidth,
        'line-opacity': TRACK_LINE_STYLE.outlineOpacity,
      },
    },
    {
      id: ids.lineId,
      type: 'line',
      source: sourceId,
      layout: withVisibility(visibility),
      paint: {
        'line-color': lineColor,
        'line-width': TRACK_LINE_STYLE.lineWidth,
      },
    },
  ]

  if (includeDirectionArrows) {
    orderedLayers.push(
      buildTrackDirectionArrowLayer({
        id: ids.arrowId,
        sourceId,
        visibility,
      }),
    )
  }

  return {
    ids,
    orderedLayers,
    layerIds: includeDirectionArrows
      ? [ids.touchTargetId, ids.outlineId, ids.lineId, ids.arrowId]
      : [ids.touchTargetId, ids.outlineId, ids.lineId],
  }
}
